import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const AIEthicsGovernance = () => {
  return (
    <>
      <SEO 
        title="AI Ethics & Governance - Responsible AI Implementation"
        description="Implement responsible AI practices with comprehensive ethics frameworks, governance policies, and compliance strategies. Ensure fairness, transparency, and accountability in your AI systems."
        keywords="AI ethics, AI governance, responsible AI, AI compliance, ethical AI, AI transparency, AI accountability, bias mitigation, AI regulations"
      />

      {/* Hero Section */}
      <section className="hero-section bg-gradient-to-br from-pearl-white via-alabaster to-blush-light/10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 animate-fade-in">
              <span className="px-4 py-2 bg-primary-red/10 text-primary-red rounded-full text-sm font-semibold">
                AI Hub
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              AI Ethics & <span className="text-gradient">Governance</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Build trust and ensure responsible AI implementation with comprehensive ethics frameworks, governance policies, and compliance strategies that align with global standards.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Start Ethics Assessment
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Ethics Matters */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Why AI Ethics & Governance Matter</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                In an era of increasing AI deployment, ethical considerations and proper governance are essential for sustainable success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                  title: 'Build Trust',
                  description: 'Establish credibility with stakeholders through transparent and ethical AI practices'
                },
                {
                  icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
                  title: 'Mitigate Risks',
                  description: 'Identify and address potential biases, fairness issues, and compliance concerns'
                },
                {
                  icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                  title: 'Ensure Compliance',
                  description: 'Meet regulatory requirements and industry standards for AI deployment'
                },
                {
                  icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                  title: 'Accelerate Adoption',
                  description: 'Enable faster AI deployment with clear ethical guidelines and governance frameworks'
                }
              ].map((item, index) => (
                <div key={index} className="card text-center hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-charcoal">{item.title}</h3>
                  <p className="text-sm text-titanium">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Framework Components */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Comprehensive Ethics Framework</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Our AI ethics framework encompasses all critical dimensions of responsible AI development and deployment
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {[
                {
                  title: 'Fairness & Bias Mitigation',
                  description: 'Identify and eliminate algorithmic bias to ensure equitable outcomes across all user groups',
                  features: [
                    'Bias detection and measurement',
                    'Fairness metrics implementation',
                    'Demographic parity analysis',
                    'Disparate impact assessment'
                  ]
                },
                {
                  title: 'Transparency & Explainability',
                  description: 'Make AI decisions understandable and auditable for stakeholders and end users',
                  features: [
                    'Model interpretability tools',
                    'Decision traceability',
                    'Explainable AI (XAI) techniques',
                    'Documentation standards'
                  ]
                },
                {
                  title: 'Privacy & Data Protection',
                  description: 'Safeguard sensitive information and comply with global privacy regulations',
                  features: [
                    'Privacy-preserving AI techniques',
                    'Data minimization strategies',
                    'GDPR, CCPA compliance',
                    'Differential privacy implementation'
                  ]
                },
                {
                  title: 'Accountability & Oversight',
                  description: 'Establish clear responsibility structures and monitoring systems for AI operations',
                  features: [
                    'Governance committee formation',
                    'Responsibility assignment',
                    'Continuous monitoring systems',
                    'Incident response protocols'
                  ]
                },
                {
                  title: 'Safety & Security',
                  description: 'Ensure AI systems are robust, reliable, and protected against adversarial attacks',
                  features: [
                    'Adversarial robustness testing',
                    'Safety constraint implementation',
                    'Secure model deployment',
                    'Vulnerability assessment'
                  ]
                },
                {
                  title: 'Human-Centered Design',
                  description: 'Keep human values and wellbeing at the center of AI system design and operation',
                  features: [
                    'Human oversight mechanisms',
                    'User consent management',
                    'Impact assessment',
                    'Stakeholder engagement'
                  ]
                }
              ].map((component, index) => (
                <div key={index} className="card hover:shadow-xl transition-all duration-300">
                  <h3 className="text-xl font-bold mb-3 text-charcoal">{component.title}</h3>
                  <p className="text-titanium mb-4">{component.description}</p>
                  <ul className="space-y-2">
                    {component.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-titanium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Governance Implementation */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="section-title">Governance Implementation Process</h2>
                <p className="text-titanium mb-8">
                  Our structured approach ensures systematic integration of ethics and governance principles into your AI operations, from strategy to execution.
                </p>

                <div className="space-y-6">
                  {[
                    {
                      step: '01',
                      title: 'Ethics Assessment',
                      description: 'Evaluate current AI practices, identify ethical risks, and establish baseline metrics'
                    },
                    {
                      step: '02',
                      title: 'Framework Design',
                      description: 'Develop customized ethics policies, governance structures, and decision-making protocols'
                    },
                    {
                      step: '03',
                      title: 'Implementation',
                      description: 'Deploy governance tools, train teams, and integrate ethical considerations into workflows'
                    },
                    {
                      step: '04',
                      title: 'Monitoring & Improvement',
                      description: 'Continuously assess performance, gather feedback, and refine governance practices'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-primary-red text-white rounded-lg flex items-center justify-center font-bold">
                          {item.step}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-2">{item.title}</h3>
                        <p className="text-titanium text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-alabaster rounded-2xl p-8">
                <h3 className="text-xl font-bold mb-6 text-charcoal">Key Governance Components</h3>
                <div className="space-y-4">
                  {[
                    { title: 'Ethics Committee', detail: 'Cross-functional oversight body' },
                    { title: 'Policy Framework', detail: 'Comprehensive ethical guidelines' },
                    { title: 'Risk Assessment', detail: 'Continuous ethical risk evaluation' },
                    { title: 'Audit Trails', detail: 'Complete decision documentation' },
                    { title: 'Training Programs', detail: 'Ethics education for all teams' },
                    { title: 'Reporting Mechanisms', detail: 'Clear escalation pathways' }
                  ].map((item, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border border-platinum">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-charcoal">{item.title}</h4>
                          <p className="text-sm text-titanium">{item.detail}</p>
                        </div>
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Standards & Compliance */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Standards & Compliance</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Align your AI systems with recognized international standards and regulatory frameworks
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  standard: 'EU AI Act',
                  description: 'Risk-based regulatory framework for AI systems in the European Union',
                  category: 'Regulation'
                },
                {
                  standard: 'ISO/IEC 42001',
                  description: 'International standard for AI management systems',
                  category: 'Standard'
                },
                {
                  standard: 'NIST AI RMF',
                  description: 'AI Risk Management Framework by US National Institute of Standards',
                  category: 'Framework'
                },
                {
                  standard: 'IEEE 7000 Series',
                  description: 'Standards for ethical considerations in system design',
                  category: 'Standard'
                },
                {
                  standard: 'OECD AI Principles',
                  description: 'International principles for responsible stewardship of trustworthy AI',
                  category: 'Principles'
                },
                {
                  standard: 'ISO/IEC 23894',
                  description: 'Guidance on risk management for AI systems',
                  category: 'Standard'
                }
              ].map((item, index) => (
                <div key={index} className="card hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-blush-light/40 text-primary-red text-xs rounded-full font-semibold">
                      {item.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-charcoal">{item.standard}</h3>
                  <p className="text-sm text-titanium">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Industry-Specific Ethics Solutions</h2>
              <p className="section-subtitle max-w-3xl mx-auto">
                Tailored ethical frameworks addressing unique challenges across different sectors
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  industry: 'Healthcare',
                  challenges: [
                    'Patient data privacy and confidentiality',
                    'Medical decision-making transparency',
                    'Clinical trial fairness and equity',
                    'Life-critical system accountability'
                  ],
                  link: '/solutions/healthcare-life-sciences'
                },
                {
                  industry: 'Financial Services',
                  challenges: [
                    'Fair lending and credit decisions',
                    'Algorithmic trading oversight',
                    'Customer data protection',
                    'Regulatory compliance (SOX, FINRA)'
                  ],
                  link: '/solutions/financial-services'
                },
                {
                  industry: 'Government & Public Sector',
                  challenges: [
                    'Citizen rights and civil liberties',
                    'Public service fairness and equity',
                    'Surveillance and privacy balance',
                    'Democratic accountability'
                  ],
                  link: '/solutions/government-public-sector'
                },
                {
                  industry: 'Retail & E-commerce',
                  challenges: [
                    'Personalization vs. privacy',
                    'Dynamic pricing fairness',
                    'Consumer manipulation prevention',
                    'Data consent management'
                  ],
                  link: '/solutions/retail-ecommerce'
                }
              ].map((item, index) => (
                <div key={index} className="card hover:shadow-xl transition-all duration-300">
                  <h3 className="text-xl font-bold mb-4 text-charcoal">{item.industry}</h3>
                  <p className="text-sm text-titanium mb-4">Key Ethical Considerations:</p>
                  <ul className="space-y-2 mb-6">
                    {item.challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-titanium">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={item.link} className="text-primary-red font-semibold hover:underline inline-flex items-center">
                    Explore Solutions
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-alabaster to-blush-light/10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Business Benefits</h2>
              <p className="section-subtitle">
                Strong AI ethics and governance deliver tangible value to your organization
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { metric: '45%', label: 'Reduction in AI-related risks and incidents' },
                { metric: '60%', label: 'Faster regulatory approval and compliance' },
                { metric: '3x', label: 'Increase in stakeholder trust and confidence' },
                { metric: '50%', label: 'Improvement in AI project success rates' }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-4xl font-bold text-primary-red mb-2">{item.metric}</div>
                  <div className="text-titanium">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related AI Hub Services */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Related AI Capabilities</h2>
              <p className="section-subtitle">
                Explore our comprehensive AI Hub services
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'AI Readiness Assessment',
                  description: 'Evaluate your organization\'s preparedness for ethical AI deployment',
                  link: '/ai-hub/ai-readiness'
                },
                {
                  title: 'MLOps Framework',
                  description: 'Implement responsible AI operations with built-in governance controls',
                  link: '/ai-hub/mlops-framework'
                },
                {
                  title: 'Intelligent Automation',
                  description: 'Automate processes with ethical considerations built into every workflow',
                  link: '/ai-hub/intelligent-automation'
                }
              ].map((service, index) => (
                <Link key={index} to={service.link} className="card hover:shadow-xl transition-all duration-300 group">
                  <h3 className="text-lg font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-titanium mb-4">{service.description}</p>
                  <span className="text-primary-red font-semibold inline-flex items-center">
                    Learn More
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-red text-white">
        <div className="page-container text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Build Trustworthy AI Systems
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Partner with us to implement comprehensive ethics frameworks and governance structures that ensure your AI systems are responsible, transparent, and aligned with your values.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Ethics Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIEthicsGovernance;